import { useState } from "react";
import IntranetLayout from "@/components/IntranetLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { HelpCircle, Send, ChevronDown, ChevronUp, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

const statusBadge: Record<string, "default" | "secondary" | "outline"> = {
  pending: "secondary",
  answered: "default",
  closed: "outline",
};

const EmployeeQuestions = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState<Record<string, string>>({});
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "answered">("pending");

  const { data: questions = [], isLoading } = useQuery({
    queryKey: ["emp-questions", statusFilter],
    queryFn: async () => {
      let q = supabase.from("technical_questions").select("*, companies(name)").order("created_at", { ascending: false });
      if (statusFilter !== "all") q = q.eq("status", statusFilter);
      const { data } = await q;
      return data ?? [];
    },
  });

  const { data: responses = [] } = useQuery({
    queryKey: ["emp-responses"],
    queryFn: async () => { const { data } = await supabase.from("question_responses").select("*").order("created_at", { ascending: true }); return data ?? []; },
  });

  const sendReply = useMutation({
    mutationFn: async ({ questionId, content }: { questionId: string; content: string }) => {
      const { error: rErr } = await supabase.from("question_responses").insert({ question_id: questionId, content: content.trim(), responded_by: user!.id });
      if (rErr) throw rErr;
      const { data: question } = await supabase.from("technical_questions").select("company_id, title").eq("id", questionId).single();
      const { error: qErr } = await supabase.from("technical_questions").update({ status: "answered" }).eq("id", questionId);
      if (qErr) throw qErr;
      if (question) {
        await supabase.from("notifications").insert({ company_id: question.company_id, type: "question_answered", title: `Your question was answered`, message: question.title, related_id: questionId });
      }
    },
    onSuccess: (_, { questionId }) => {
      setReplyContent((prev) => ({ ...prev, [questionId]: "" }));
      qc.invalidateQueries({ queryKey: ["emp-questions"] });
      qc.invalidateQueries({ queryKey: ["emp-responses"] });
      toast.success("Reply sent to client");
    },
    onError: () => toast.error("Failed to send reply"),
  });

  return (
    <IntranetLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-foreground">Client Questions</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">Technical inquiries from clients</p>
        </div>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {(["pending", "answered", "all"] as const).map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-1.5 rounded-md text-xs font-medium font-body transition-all ${statusFilter === s ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" /></div>
      ) : questions.length === 0 ? (
        <Card><CardContent className="p-16 text-center"><HelpCircle className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" /><p className="text-muted-foreground font-body">{statusFilter === "pending" ? "No pending questions — great work!" : "No questions found."}</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {questions.map((q) => {
            const qResponses = responses.filter((r) => r.question_id === q.id);
            const expanded = expandedId === q.id;
            const reply = replyContent[q.id] ?? "";
            return (
              <Card key={q.id}>
                <button onClick={() => setExpandedId(expanded ? null : q.id)} className="w-full p-5 flex items-center gap-4 text-left hover:bg-muted/50 transition-all rounded-t-lg">
                  <MessageSquare className="w-5 h-5 text-teal/50 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground font-body font-medium truncate">{q.title}</p>
                    <p className="text-xs text-muted-foreground font-body mt-0.5">
                      {(q.companies as { name: string } | null)?.name ?? "Unknown"} · {new Date(q.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant={statusBadge[q.status] || "outline"}>{q.status}</Badge>
                  {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </button>

                {expanded && (
                  <CardContent className="pt-0 pb-5 px-5 border-t border-border">
                    <div className="py-4">
                      <p className="text-[11px] font-bold tracking-[2px] uppercase text-muted-foreground font-body mb-2">Client's Question</p>
                      <p className="text-sm text-foreground/80 font-body whitespace-pre-wrap">{q.content}</p>
                    </div>

                    {qResponses.length > 0 && (
                      <div className="space-y-3 mb-4">
                        <p className="text-[11px] font-bold tracking-[2px] uppercase text-teal/60 font-body">Previous Responses</p>
                        {qResponses.map((r) => (
                          <div key={r.id} className="p-4 bg-teal/5 rounded-xl border border-teal/10">
                            <p className="text-sm text-foreground/80 font-body whitespace-pre-wrap">{r.content}</p>
                            <p className="text-xs text-muted-foreground/60 font-body mt-2">{new Date(r.created_at).toLocaleDateString()}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="space-y-3">
                      <p className="text-[11px] font-bold tracking-[2px] uppercase text-muted-foreground font-body">Your Reply</p>
                      <Textarea value={reply} onChange={(e) => setReplyContent((prev) => ({ ...prev, [q.id]: e.target.value }))} placeholder="Type your response to the client..." rows={4} />
                      <Button onClick={() => { if (reply.trim()) sendReply.mutate({ questionId: q.id, content: reply }); }} disabled={!reply.trim() || sendReply.isPending} className="bg-teal hover:bg-teal-light text-white gap-2">
                        <Send className="w-3.5 h-3.5" /> Send Reply
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </IntranetLayout>
  );
};

export default EmployeeQuestions;
